import { Button } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents';
import { MODAL_OVERLAY_Z_INDEX } from '~/constants/layout';
import { TextAreaField } from '~/components/shared/forms';
import React, { useState } from 'react';
import { motion } from 'framer-motion';

import { colors } from '@tempmee/tailwind';
import { Colors } from '../themes/colors';
import { useGetCancellationReasons, useSubmitCancellationFeedback } from '~/domain/JobCancellation';
import browserHistory from '~/browserHistory';
import AnimatedCheckCircle from './shared/AnimatedCheckCircle';
import checkedCircleIcon from '../images/checkedCircleIcon.svg';

import { useParams } from 'react-router';
import { Box, Text } from '~/commonComponents';
import { OutlineCircleIcon } from '@tempmee/react-icons';

export default function CancellationFeedbackLegacy() {
  const params = useParams();
  const { jobId } = params;

  const { data: feedbackReasons, isLoading } = useGetCancellationReasons();
  const { mutate: submitCancellationFeedback, isPending: isSubmitting } =
    useSubmitCancellationFeedback();

  const [cancellationFeedback, setCancellationFeedback] = useState('');
  const [cancellationReason, setsCancellationReason] = useState('');

  const buttonValidation =
    cancellationFeedback === '' ||
    (cancellationFeedback === 'other' && cancellationReason?.length < 3);

  const submitFeedback = () => {
    if (!jobId) {
      return;
    }

    submitCancellationFeedback(
      {
        jobId,
        cancellationFeedback,
        cancellationReason,
      },
      {
        onSuccess: () => browserHistory.push('/dashboard/home'),
      },
    );
  };

  return (
    <div
      style={{
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        zIndex: MODAL_OVERLAY_Z_INDEX,
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
      }}
    >
      <div
        style={{
          backgroundColor: colors.shades[0],
          alignContent: 'center',
          justifyContent: 'center',
          maxWidth: 520,
          minWidth: 450,
          width: '35%',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          marginTop: 30,
          marginBottom: 50,
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.3)',
          shadowColor: Colors.neutral_400,
          shadowOpacity: 0.3,
          shadowOffset: {
            height: 3,
            width: 0,
          },
        }}
      >
        {isLoading ? (
          <CenteredSpinner />
        ) : (
          <div
            style={{
              width: '80%',
              alignSelf: 'center',
              overflowY: 'auto',
              maxHeight: '95vh',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 42,
                marginBottom: 18,
              }}
            >
              <AnimatedCheckCircle />
            </div>

            <div
              style={{
                color: colors.neutral[600],
                fontSize: 18,
                fontFamily: 'Nunito',
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              Cancellation Successful!
            </div>

            <Box marginBlock={25} width="100%">
              <Text color={colors.neutral[500]} fontSize={18} textAlign="center">
                Help us improve your experience by sharing
                <br />
                why this shift was cancelled. Please select
                <br />
                one option below.
              </Text>
            </Box>

            {feedbackReasons.map((reason) => (
              <div
                key={reason.code}
                data-cy={`cancellation-reason-${reason.code}`}
                onClick={() => setCancellationFeedback(reason.code)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: 18,
                  cursor: 'pointer',
                  width: '100%',
                  paddingInline: 16,
                }}
              >
                {cancellationFeedback === reason.code ? (
                  <img src={checkedCircleIcon} style={{ marginRight: 10 }} alt="Selected" />
                ) : (
                  <OutlineCircleIcon
                    size={24}
                    color={colors.secondary[500]}
                    style={{ marginRight: 10 }}
                  />
                )}
                <div
                  style={{
                    color: colors.neutral[600],
                    fontSize: 16,
                    fontFamily: 'Nunito',
                    cursor: 'pointer',
                  }}
                >
                  {reason.name}
                </div>
              </div>
            ))}

            <motion.div
              initial="hide"
              animate={cancellationFeedback === 'other' ? 'show' : 'hide'}
              variants={textAreaVariants}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <TextAreaField
                data-cy="cancellation-details-textarea"
                placeholder="Provide details here..."
                height={60}
                style={{ marginTop: 20, width: '80%' }}
                value={cancellationReason}
                onChange={(e) => setsCancellationReason(e.target.value)}
              />
            </motion.div>

            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 30,
                marginBottom: 48,
              }}
            >
              <Button
                data-cy="submit-cancellation-feedback"
                disabled={isSubmitting || buttonValidation}
                size="md"
                onClick={() => submitFeedback()}
              >
                Submit
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const textAreaVariants = {
  show: {
    maxHeight: 130,
    opacity: 1,
    display: 'flex',
  },
  hide: {
    maxHeight: 0,
    opacity: 0,
    transitionEnd: { display: 'none' },
  },
};
